import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaPray, 
  FaTruck, 
  FaShieldAlt, 
  FaUsers, 
  FaDrumstickBite, 
  FaLeaf 
} from 'react-icons/fa';

const ServicesContainer = styled.section`
  padding: 80px 0;
  background: var(--light-gray);
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: var(--secondary-color);
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

const ServiceItem = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  padding: 40px;
  box-shadow: var(--shadow);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 200px;
  border-radius: var(--border-radius);
  overflow: hidden;
  margin-bottom: 25px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: var(--transition);
  }
  
  ${ServiceItem}:hover & img {
    transform: scale(1.1);
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--white-color);
  margin-right: 20px;
  flex-shrink: 0;
`;

const ServiceTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: var(--secondary-color);
  margin: 0;
`;

const ServiceDescription = styled.p`
  color: var(--secondary-color);
  line-height: 1.6;
  margin-bottom: 20px;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
`;

const ServiceFeature = styled.li`
  color: var(--secondary-color);
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--primary-color);
    font-weight: bold;
  }
`;

const ServicesSection = () => {
  const services = [
    
    {
      icon: <FaPray />,
      title: 'Kurban Hizmetleri',
      description: 'Kurban bayramında güvenilir ve sağlıklı hayvan seçimi',
      features: ['Sağlık kontrolü', 'Veteriner onayı', 'Teslimat', 'Kesim hizmeti'],
      image: '/images/services/kurban-hizmetleri.jpg'
    },
    {
      icon: <FaTruck />,
      title: 'Nakliye Hizmetleri',
      description: 'Hayvanlarınızı güvenli şekilde istediğiniz yere taşıyoruz',
      features: ['Özel araçlar', 'Güvenli taşıma', 'Hızlı teslimat', '7/24 hizmet'],
      image: '/images/services/nakliye-hizmetleri.jpg'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Sağlık Garantisi',
      description: 'Tüm hayvanlarımız veteriner kontrolünden geçer ve sağlık garantisi verir',
      features: ['Veteriner kontrolü', 'Aşı kayıtları', 'Sağlık raporu', 'Garanti belgesi'],
      image: '/images/services/saglik-garantisi.jpg'
    },
    
  ];

  return (
    <ServicesContainer>
      <Container>
        <SectionTitle>Hizmetlerimiz</SectionTitle>
        <SectionSubtitle>
          Besi çiftliğimizde sunduğumuz kapsamlı hizmetler ile 
          müşterilerimizin tüm ihtiyaçlarını karşılıyoruz.
        </SectionSubtitle>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceItem
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <ServiceImage>
                <img src={service.image} alt={service.title} />
              </ServiceImage>
              <ServiceHeader>
                <ServiceIcon>{service.icon}</ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
              </ServiceHeader>
              
              <ServiceDescription>{service.description}</ServiceDescription>
              
              <ServiceFeatures>
                {service.features.map((feature, featureIndex) => (
                  <ServiceFeature key={featureIndex}>
                    {feature}
                  </ServiceFeature>
                ))}
              </ServiceFeatures>
            </ServiceItem>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesContainer>
  );
};

export default ServicesSection; 